import React, { useEffect, useRef, useState } from 'react';
import { getSelfExpOptions, UNKNOWN_OPTION } from './selfExpOptions';
import DirectionNote from './DirectionNote';

// 自己説明「選択式回答」＝2026-07-27週例の決定を受けた最も制約された版。
// 空欄を自由記述させず、文中の空欄（？？？）をボックスにし、押すとプルダウンで
// 候補から選べるようにして説明文を組み立てる。
// 候補データが無いモデル・phaseでは親（SelfExp）側で従来の自由記述にフォールバックする
// （このコンポーネントはgetSelfExpOptionsがnullを返す場合に備え、その場合は何も描画しない）。
//
// 文型はカテゴリスキーマ基準（2026-09-28週例で変更。旧＝EBS誤り可視化モデルの4要素対応）。
// 学習者が持つと仮定される既存知識（カテゴリスキーマ）と、ガイドで誤りが解消される過程を
// 紐づけて、誤りの理由を説明させる：
//   〔angle〕=誤りが顕在化される視点／〔state〕=そこで見えるおかしさ／
//   〔drawn〕=②既存知識から導かれる形＝どう描かれているか／〔knowledge〕=①既存知識（例「皿は丸い」）／
//   〔fix〕=④あるべきデッサンの描き方（shape）または視点の直し方（gaze）。
//
// ★一人称を使わない（2026-10-02変更・重要）★
// 旧文型は「わたしは〔knowledge〕と思っていたので…」と書き出していたが、(1)指摘の対象は他者の
// デッサンなので「わたしは」は不自然、(2)その既存知識を持つ学習者と持たない学習者がいるため
// 自分の思い込みとして断定させるのは測定として不適切、の2点で廃止した。
// 新文型は「3Dモデルで顕在化したおかしさの観察 → 誤りの帰属 → カテゴリスキーマの提示 → 直し方」
// の順に並べた記述文（主語を立てない）で、カテゴリスキーマによる誤りを提示する説明手法になる。
//
// ★「正しいモチーフの見え方」（旧 seen）は置かない（2026-10-03変更・重要）★
// 形状変更では旧③と④がほぼ同義（「皿は楕円に見える」↔「楕円のように横長に」）で二度測りになり、
// ③で④を根拠づけると「モチーフを見たから直せる」になってガイドとの紐づけから外れるため廃止した。
// **④の根拠はガイドの結果に置く**：shape-changeは「形を変えると3Dモデルのおかしさが消えるので」、
// gaze-controlは「視点を〔fix〕と、おかしな箇所がモチーフと同じに見えたので」。③が担っていた「既存知識由来の形と
// 実際の見え方の区別」は、selfExpOptions.js 側で fix の誤答に対になる語を入れて測る。
//
// ▼ここを編集すれば文章・空欄の位置を変えられる（配列の要素1つ＝1行として表示）▼
// text＝そのまま表示される地の文、blank＝空欄（selfExpOptions.jsの同名キーの選択肢が入る）
const SENTENCE_LINES_BY_PHASE = {
  'shape-change': [
    [
      { text: 'デッサンの3Dモデルを' },
      { blank: 'angle' },
      { text: '見てみると、' },
      { blank: 'state' },
      { text: '。' },
    ],
    [
      { text: 'このおかしさは' },
      { blank: 'drawn' },
      { text: '点にあり、' },
      { blank: 'knowledge' },
      { text: 'という知識にとらわれた誤りともいえる。' },
    ],
    [
      { text: '形を変えると3Dモデルのおかしさが消えるので、' },
      { blank: 'fix' },
      { text: '描きなおせばよい。' },
    ],
  ],
  'gaze-control': [
    [
      { text: 'デッサンの3Dモデルを' },
      { blank: 'angle' },
      { text: '見てみると、' },
      { blank: 'state' },
      { text: '。' },
    ],
    [
      { text: 'このおかしさは' },
      { blank: 'drawn' },
      { text: '点にあり、' },
      { blank: 'knowledge' },
      { text: 'という知識にとらわれた誤りともいえる。' },
    ],
    [
      { text: '視点を' },
      { blank: 'fix' },
      { text: 'と、おかしな箇所がモチーフと同じに見えたので、このデッサンは別の視点から見た形を描いている。' },
    ],
  ],
};
// ▲文章・空欄の位置はここまで。空欄ごとの選択肢は selfExpOptions.js の SELF_EXP_OPTIONS▲

// initialSelections / disabled: 回答済みの問へ戻ってきたとき、当時の選択を
// 復元して表示し、プルダウンの操作だけ止める（決定ボタンは親側で無効にする）。
export default function SelfExpSelect({ phase, errorGlbPath, onChange, initialSelections = null, disabled = false }) {
  const options = getSelfExpOptions(phase, errorGlbPath);
  const [selections, setSelections] = useState(() => initialSelections || {});

  const lines = SENTENCE_LINES_BY_PHASE[phase] || SENTENCE_LINES_BY_PHASE['shape-change'];
  const blankSlots = lines.flat().filter((p) => p.blank).map((p) => p.blank);

  const buildText = (sel) =>
    lines
      .map((line) => line.map((p) => (p.text != null ? p.text : (sel[p.blank] || '？？？'))).join(''))
      .join('');

  const notify = (sel) => {
    // filled / total は「あと何か所選べばよいか」の進捗表示（SelfExpPanel）に使う
    const filled = blankSlots.filter((s) => sel[s]).length;
    if (typeof onChange === 'function') {
      onChange({
        text: buildText(sel),
        selections: sel,
        complete: filled === blankSlots.length,
        filled,
        total: blankSlots.length,
      });
    }
  };

  // 復元した選択を親（進捗表示・回答文）にも一度だけ反映する
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (notifiedRef.current || !options) return;
    notifiedRef.current = true;
    if (initialSelections && Object.keys(initialSelections).length > 0) notify(initialSelections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  if (!options) return null;

  const handleSelect = (slot) => (e) => {
    const next = { ...selections, [slot]: e.target.value };
    setSelections(next);
    notify(next);
  };

  return (
    <>
    <DirectionNote glbPath={errorGlbPath} />
    <div className="selfexp-select__sentence">
      {lines.map((line, li) => (
        <p key={li}>
          {line.map((part, i) =>
            part.text != null ? (
              <React.Fragment key={i}>{part.text}</React.Fragment>
            ) : (
              <select
                key={i}
                className="selfexp-select__blank"
                value={selections[part.blank] || ''}
                onChange={handleSelect(part.blank)}
                disabled={disabled}
              >
                <option value="">？？？</option>
                {(options[part.blank]?.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
                <option value={UNKNOWN_OPTION}>{UNKNOWN_OPTION}</option>
              </select>
            )
          )}
        </p>
      ))}
    </div>
    </>
  );
}
